import React, { createContext, useContext, useEffect, useReducer, useRef } from 'react'
import authReducer from './authReducer'
import { SET_AUTH_USER, SET_USER_DATA, SET_USER_CLAIMS } from '../types'
import { auth, firestore, functions } from '../../services/firebase'
import { signInWithPopup, GoogleAuthProvider, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail, FacebookAuthProvider, OAuthProvider } from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import SignupModal from '../../components/SignupModal'
import { NotificationContext } from '../notification/notificationState'
import { LanguageContext } from '../language/languageState'
import AgreementsModal from '../../components/AgreementsModal'
import { doc, onSnapshot } from 'firebase/firestore'

const googleAuthProvider = new GoogleAuthProvider()
const facebookAuthProvider = new FacebookAuthProvider()

const AuthContext = createContext()

const AuthState = ({ children }) => {
  const initialState = {
    authUser: null,
    userData: null,
    userClaims: null,
    isUserAuthenticated: null,
    authStateLoaded: false,
    userDataLoaded: false,
    userClaimsLoaded: false
  }

  const { t } = useContext(LanguageContext)

  const { toast } = useContext(NotificationContext)

  const [state, dispatch] = useReducer(authReducer, initialState)

  const [authModal, setAuthModal] = React.useState({ isOpen: false, type: 'login', prefillData: {}, onComplete: null })

  const userDataListenerRef = useRef(null)

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      dispatch({ type: SET_AUTH_USER, payload: user })
      if(user) {
        user.getIdTokenResult(true).then((idTokenResult) => {
          dispatch({ type: SET_USER_CLAIMS, payload: idTokenResult.claims || {} })
        })
        userDataListenerRef.current = onSnapshot(doc(firestore, `users/${user.uid}`), (doc) => {
          if(doc.exists()) {
            dispatch({ type: SET_USER_DATA, payload: doc.data() })
          }
        })
      } else {
        userDataListenerRef.current?.()
        dispatch({ type: SET_USER_DATA, payload: null })
        dispatch({ type: SET_USER_CLAIMS, payload: null })
      }
    })
    return () => {
      unsubscribe()
    }
  }, [])

  const promptAuth = (type = 'login', prefillData = {}, onComplete) => {
    setAuthModal({ isOpen: true, prefillData, type, onComplete })
  }

  const hideAuthModal = () => {
    setAuthModal({ isOpen: false, type: 'login', prefillData: {}, onComplete: null })
  }

  const signInWithEmail = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password)
      return userCredential
    } catch (error) {
      const message = error.code ? t(`error.${error.code}`) : error.message
      toast({ message, variant: 'error' })
    }
  }

  const signUpWithEmail = async (email, password) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password)
      return userCredential
    } catch (error) {
      const message = error.code ? t(`error.${error.code}`) : error.message
      toast({ message, variant: 'error' })
    }
  }

  const signInWithMethod = async (method) => {
    try {
      switch(method) {
        case 'google':
          return await signInWithPopup(auth, googleAuthProvider)
        case 'apple':
          const provider = new OAuthProvider('apple.com')
          provider.addScope('email')
          provider.addScope('name')
          provider.setCustomParameters({
            locale: 'fr'
          })
          return await signInWithPopup(auth, provider)
        case 'facebook':
          facebookAuthProvider.addScope('email')
          facebookAuthProvider.setCustomParameters({ display: 'popup' })
          facebookAuthProvider.setDefaultLanguage('fr_FR')
          return await signInWithPopup(auth, facebookAuthProvider)
        default:
          throw new Error('Invalid Sign In Method ' + method)
      }
    } catch (error) {
      const message = error.code ? t(`error.${error.code}`) : error.message
      toast({ message, variant: 'error' })
      return null
    }
  }

  const signOut = async () => {
    await auth.signOut()
  }

  const updateUser = async (data) => {
    try {
      const updateUser = httpsCallable(functions, "updateUser")
      const updatedUserResponse = await updateUser(data)
      if(updatedUserResponse.data) {
        dispatch({ type: SET_USER_DATA, payload: updatedUserResponse.data })
      }
    } catch (error) {
      const message = error.code ? t(`error.${error.code}`) : error.message
      toast({ message, variant: 'error' })
    }
  }

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email)
      return {}
    } catch (error) {
      return {
        error: error
      }
    }
  }

  const handleAgreementsSubmit = async () => {
    try {
      const submitConsent = httpsCallable(functions, "submitConsent")
      await submitConsent()
      const tokenResult = await auth.currentUser.getIdTokenResult(true)
      dispatch({ type: SET_USER_CLAIMS, payload: tokenResult.claims })
    } catch (error) {
      const message = error.code ? t(`error.${error.code}`) : error.message
      toast({ message, variant: 'error' })
    }
  }

  return (
    <AuthContext.Provider value={{
      authUser: state.authUser,
      userData: state.userData,
      isUserAuthenticated: state.isUserAuthenticated,
      authStateLoaded: state.authStateLoaded,
      userDataLoaded: state.userDataLoaded,
      signInWithEmail,
      signInWithMethod,
      signUpWithEmail,
      updateUser,
      signOut,
      promptAuth,
      resetPassword
    }}>
      {<SignupModal showModal={authModal.isOpen} onClose={hideAuthModal} type={authModal.type} prefillData={authModal.prefillData} onComplete={authModal.onComplete} />}
      {state.authStateLoaded && state.userClaimsLoaded && !state.userClaims?.conditions_accepted && <AgreementsModal onSubmit={handleAgreementsSubmit} />}
      {children}
    </AuthContext.Provider>
  )
}

export default AuthState
export { AuthContext }
